import { useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  Avatar,
  FieldDiff,
  Spinner,
} from '@wundo/ui';
import { Code, ExternalLink } from 'lucide-react';
import { fetchChangeLogDiff, type ChangeLog } from '@/lib/changeLogs.ts';
import { formatRelativeTime } from '@/utils/dateUtils.ts';

export interface ChangeLogCardProps {
  log: ChangeLog;
  /** Where the changed record lives, when the console has a page for it. */
  entityHref?: string;
  /** Open, as the list around it has it. What makes the diff worth fetching. */
  expanded: boolean;
}

const ACTION_VERB: Record<ChangeLog['action'], string> = {
  create: 'created',
  update: 'edited',
  delete: 'deleted',
};

const ACTION_TONE: Record<ChangeLog['action'], string> = {
  create: 'text-success',
  update: 'text-ink',
  delete: 'text-danger',
};

function actorName(log: ChangeLog): string {
  const trimmed = log.user_name?.trim();
  if (trimmed) return trimmed;
  return log.user_id ? '(unknown user)' : 'File sync';
}

/** The one-line answer to "what did this touch", without opening the diff. */
function summaryOf(log: ChangeLog): string {
  if (log.action === 'create') {
    const keyCount = Object.keys(log.data ?? {}).length;
    return `Initial values for ${keyCount} ${keyCount === 1 ? 'field' : 'fields'}`;
  }
  if (log.action === 'delete') return 'Last known values preserved';

  const keys = Object.keys(log.data ?? {});
  if (keys.length === 0) return 'No changes recorded';
  return `${keys.length} ${keys.length === 1 ? 'field' : 'fields'}: ${keys.join(', ')}`;
}

/**
 * One recorded edit, folded shut.
 *
 * The before/after values are fetched only when the card is opened: a page of
 * twenty-five entries would otherwise be twenty-five diffs nobody asked for,
 * and most of them are never read at all.
 */
export function ChangeLogCard({ log, entityHref, expanded }: ChangeLogCardProps) {
  const router = useRouter();
  const systemActor = log.user_id === null;

  const {
    data: diff,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['changeLogDiff', log.id],
    queryFn: () => fetchChangeLogDiff(log.id),
    enabled: expanded,
  });

  const diffKeys = Object.keys(diff?.after ?? diff?.before ?? {});

  return (
    <AccordionItem value={log.id}>
      <AccordionTrigger className='gap-3 px-3.5 py-2.5 text-sm font-normal'>
        {systemActor ? (
          <span
            title='File sync'
            className='inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-fill text-ink-secondary'
          >
            <Code className='size-4' aria-hidden='true' />
          </span>
        ) : (
          <Avatar name={actorName(log)} seed={log.user_id ?? actorName(log)} />
        )}

        <span className='min-w-0 flex-1'>
          <span className='block text-sm text-ink'>
            <span className='font-semibold'>{actorName(log)}</span>
            <span className={`mx-1 font-semibold ${ACTION_TONE[log.action]}`}>
              {ACTION_VERB[log.action]}
            </span>
            {log.entity_type}{' '}
            <span className='font-mono text-ink-secondary'>
              {log.entity_id.slice(0, 8)}
            </span>
          </span>
          <span className='mt-0.5 block truncate text-xs font-normal text-ink-muted'>
            {summaryOf(log)}
          </span>
        </span>

        <span
          className='shrink-0 text-xs font-normal text-ink-muted'
          title={new Date(log.created_at).toLocaleString()}
        >
          {formatRelativeTime(log.created_at)}
        </span>
      </AccordionTrigger>

      <AccordionPanel>
        <div className='flex flex-col gap-5 border-t border-line bg-fill-faint px-3.5 py-3.5'>
          {expanded && isPending && <Spinner className='size-4 text-ink-muted' />}

          {isError && <p className='text-sm text-danger'>Failed to load diff</p>}

          {diff && diffKeys.length === 0 && (
            <p className='text-sm text-ink-muted'>No fields recorded for this entry.</p>
          )}

          {diffKeys.map((key) => (
            <FieldDiff
              key={key}
              label={key}
              before={diff?.before?.[key]}
              after={diff?.after?.[key]}
            />
          ))}

          {entityHref && log.action !== 'delete' && (
            <div className='border-t border-line pt-3'>
              {/*
               * A real link, so it opens in a new tab like any other; the plain
               * left click is handed to the router instead of the browser.
               */}
              <a
                href={entityHref}
                className='inline-flex items-center gap-1 text-xs text-ink underline underline-offset-2'
                onClick={(event) => {
                  if (
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.button !== 0
                  ) {
                    return;
                  }
                  event.preventDefault();
                  void router.navigate({ href: entityHref });
                }}
              >
                <ExternalLink className='size-3.5' aria-hidden='true' />
                View entity
              </a>
            </div>
          )}
        </div>
      </AccordionPanel>
    </AccordionItem>
  );
}
